import { useEffect, useRef, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { STFF, STFF_TEMPLATE } from "@/models/stff";
import { validateMinDate } from "@/utils/util";
import { formatDateOnly } from "@/utils/dates";

export function convertEthnicityFromBackend(
  value: string | boolean | undefined | null,
  missingIndicator: string | boolean | undefined | null
): string | undefined {
  if (value === true || value === "Y") return "Y";
  if (value === false || value === "N") return "N";
  if (missingIndicator === true || missingIndicator === "Y") return "";
  return undefined;
}

export function getMostRecentStaff(records: STFF[]): STFF | undefined {
  if (!records || records.length === 0) return undefined;
  return [...records].sort((a, b) => {
    const da = new Date(a.EffectiveStartDate ?? "").getTime();
    const db = new Date(b.EffectiveStartDate ?? "").getTime();
    return (
      (Number.isNaN(db) ? -Infinity : db) - (Number.isNaN(da) ? -Infinity : da)
    );
  })[0];
}

export function buildStaffAddDefaults(record: STFF): STFF {
  const ethnicityValue = convertEthnicityFromBackend(
    record.StaffEthnicity,
    record.StaffEthnicityMissingIndicator
  );
  const ethnicityMissingIndicator =
    ethnicityValue === "Y" || ethnicityValue === "N" ? "N" : "Y";

  const races = [
    record.StaffRace1,
    record.StaffRace2,
    record.StaffRace3,
    record.StaffRace4,
    record.StaffRace5,
  ].filter(Boolean);
  const raceMissingIndicator = races.length > 0 ? "N" : "Y";

  return {
    ...STFF_TEMPLATE,
    ReportingLEA: record.ReportingLEA,
    ProgramYear: record.ProgramYear,
    StaffLegalFirstName: record.StaffLegalFirstName,
    StaffLegalMiddleName: record.StaffLegalMiddleName,
    StaffLegalLastName: record.StaffLegalLastName,
    SEID: record.SEID,
    LocalStaffId: record.LocalStaffId,
    StaffDateOfBirth: record.StaffDateOfBirth,
    StaffGender: record.StaffGender,
    StaffHighestDegreeCode: record.StaffHighestDegreeCode,
    StaffRace1: record.StaffRace1,
    StaffRace2: record.StaffRace2,
    StaffRace3: record.StaffRace3,
    StaffRace4: record.StaffRace4,
    StaffRace5: record.StaffRace5,
    StaffLanguage1: record.StaffLanguage1,
    StaffLanguage2: record.StaffLanguage2,
    StaffLanguage3: record.StaffLanguage3,
    StaffLanguage4: record.StaffLanguage4,
    StaffLanguage5: record.StaffLanguage5,
    StaffWaiver: record.StaffWaiver,
    StaffWaiverType: record.StaffWaiverType,
    StaffWaiverIssuedDate: record.StaffWaiverIssuedDate,
    StaffWaiverExpirationDate: record.StaffWaiverExpirationDate,
    StaffPermitOrCredentialNumber: record.StaffPermitOrCredentialNumber,
    StaffPermitOrCredentialType: record.StaffPermitOrCredentialType,
    StaffPermitIssuedDate: record.StaffPermitIssuedDate,
    StaffPermitExpirationDate: record.StaffPermitExpirationDate,
    StaffCurrentEmploymentStartDate: record.StaffCurrentEmploymentStartDate,
    StaffCurrentEmploymentEndDate: record.StaffCurrentEmploymentEndDate,
    StaffOverallEmploymentStartDate: record.StaffOverallEmploymentStartDate,
    StaffEthnicity: ethnicityValue ?? "",
    StaffEthnicityMissingIndicator: ethnicityMissingIndicator,
    StaffRaceMissingIndicator: raceMissingIndicator,
    Id: "",
    EffectiveStartDate: "",
    EffectiveEndDate: "",
  };
}

export function useSTFFFormLogic(
  form: UseFormReturn<STFF, any, STFF>,
  selectedStaff: STFF | undefined
) {
  const { setValue, watch, getValues, trigger, clearErrors } = form;

  const isInternalChange = useRef(false);

  const normalizeDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!selectedStaff) return;

    const ethnicityValue = convertEthnicityFromBackend(
      selectedStaff.StaffEthnicity,
      selectedStaff.StaffEthnicityMissingIndicator
    );
    const ethnicityMissingIndicator =
      ethnicityValue === "Y" || ethnicityValue === "N" ? "N" : "Y";

    const races = [
      selectedStaff.StaffRace1,
      selectedStaff.StaffRace2,
      selectedStaff.StaffRace3,
      selectedStaff.StaffRace4,
      selectedStaff.StaffRace5,
    ].filter(Boolean);
    const raceMissingIndicator = races.length > 0 ? "N" : "Y";

    form.reset({
      ...selectedStaff,
      EffectiveStartDate: normalizeDateForInput(
        selectedStaff.EffectiveStartDate
      ),
      EffectiveEndDate: normalizeDateForInput(selectedStaff.EffectiveEndDate),
      StaffDateOfBirth: normalizeDateForInput(selectedStaff.StaffDateOfBirth),
      StaffWaiverIssuedDate: normalizeDateForInput(
        selectedStaff.StaffWaiverIssuedDate
      ),
      StaffWaiverExpirationDate: normalizeDateForInput(
        selectedStaff.StaffWaiverExpirationDate
      ),
      StaffPermitIssuedDate: normalizeDateForInput(
        selectedStaff.StaffPermitIssuedDate
      ),
      StaffPermitExpirationDate: normalizeDateForInput(
        selectedStaff.StaffPermitExpirationDate
      ),
      StaffCurrentEmploymentStartDate: normalizeDateForInput(
        selectedStaff.StaffCurrentEmploymentStartDate
      ),
      StaffCurrentEmploymentEndDate: normalizeDateForInput(
        selectedStaff.StaffCurrentEmploymentEndDate
      ),
      StaffOverallEmploymentStartDate: normalizeDateForInput(
        selectedStaff.StaffOverallEmploymentStartDate
      ),
      StaffEthnicity: ethnicityValue,
      StaffEthnicityMissingIndicator: ethnicityMissingIndicator,
      StaffRaceMissingIndicator: raceMissingIndicator,
    });
  }, [selectedStaff?.LocalStaffId, form]);

  // Watch ethnicity and update missing indicator
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "StaffEthnicity") {
        const ethnicityValue = value.StaffEthnicity;
        if (ethnicityValue === "Y" || ethnicityValue === "N") {
          setValue("StaffEthnicityMissingIndicator", "N", {
            shouldDirty: true,
          });
        } else if (ethnicityValue === "") {
          setValue("StaffEthnicityMissingIndicator", "Y", {
            shouldDirty: true,
          });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (
        name === "StaffRace1" ||
        name === "StaffRace2" ||
        name === "StaffRace3" ||
        name === "StaffRace4" ||
        name === "StaffRace5"
      ) {
        const races = getRaces();
        const missingIndicator = races.length > 0 ? "N" : "Y";
        setValue("StaffRaceMissingIndicator", missingIndicator, {
          shouldDirty: true,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  useEffect(() => {
    const currentEthnicity = getValues("StaffEthnicity");
    const currentEthnicityMissing = getValues("StaffEthnicityMissingIndicator");

    if (
      !currentEthnicityMissing &&
      currentEthnicity !== "Y" &&
      currentEthnicity !== "N"
    ) {
      setValue("StaffEthnicityMissingIndicator", "Y", { shouldDirty: false });
    }

    const races = getRaces();
    const currentRaceMissing = getValues("StaffRaceMissingIndicator");

    if (!currentRaceMissing && races.length === 0) {
      setValue("StaffRaceMissingIndicator", "Y", { shouldDirty: false });
    }
  }, []);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      // Only handle StaffWaiver changes
      if (name !== "StaffWaiver") return;

      // Prevent infinite loops
      if (isInternalChange.current) return;

      if (value.StaffWaiver === "N") {
        isInternalChange.current = true;

        // Clear errors first
        clearErrors([
          "StaffWaiverIssuedDate",
          "StaffWaiverExpirationDate",
          "StaffWaiverType",
        ]);

        // Then clear the field values without triggering validation
        setValue("StaffWaiverIssuedDate", "", {
          shouldDirty: true,
          shouldValidate: false,
        });
        setValue("StaffWaiverExpirationDate", "", {
          shouldDirty: true,
          shouldValidate: false,
        });
        setValue("StaffWaiverType", "", {
          shouldDirty: true,
          shouldValidate: false,
        });

        isInternalChange.current = false;
      }
      // When switching to "Yes", we don't need to do anything special here
      // The triggerWaiverValidation function will handle validation before submit
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, clearErrors]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (!name) return;

      if (name === "ProgramYear" && value.EffectiveStartDate) {
        trigger("EffectiveStartDate");
      }

      if (
        name === "StaffWaiverIssuedDate" &&
        value.StaffWaiverExpirationDate &&
        value.StaffWaiver === "Y"
      ) {
        trigger("StaffWaiverExpirationDate");
      }

      if (
        name === "EffectiveStartDate" &&
        value.StaffWaiverIssuedDate &&
        value.StaffWaiver === "Y"
      ) {
        trigger("StaffWaiverIssuedDate");
      }

      if (name === "StaffPermitIssuedDate" && value.StaffPermitExpirationDate) {
        trigger("StaffPermitExpirationDate");
      }

      if (name === "EffectiveStartDate" && value.StaffPermitIssuedDate) {
        trigger("StaffPermitIssuedDate");
      }

      if (
        name === "EffectiveStartDate" &&
        value.StaffCurrentEmploymentStartDate
      ) {
        trigger("StaffCurrentEmploymentStartDate");
      }

      if (
        name === "StaffCurrentEmploymentStartDate" &&
        value.StaffOverallEmploymentStartDate
      ) {
        trigger("StaffOverallEmploymentStartDate");
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  const getRaces = () => {
    const values = getValues();
    return [
      values.StaffRace1,
      values.StaffRace2,
      values.StaffRace3,
      values.StaffRace4,
      values.StaffRace5,
    ].filter(Boolean);
  };

  const setRaces = (values: string[]) => {
    setValue("StaffRace1", values[0] || "", { shouldDirty: true });
    setValue("StaffRace2", values[1] || "", { shouldDirty: true });
    setValue("StaffRace3", values[2] || "", { shouldDirty: true });
    setValue("StaffRace4", values[3] || "", { shouldDirty: true });
    setValue("StaffRace5", values[4] || "", { shouldDirty: true });
  };

  const getLanguages = () => {
    const values = getValues();
    return [
      values.StaffLanguage1,
      values.StaffLanguage2,
      values.StaffLanguage3,
      values.StaffLanguage4,
      values.StaffLanguage5,
    ].filter(Boolean);
  };

  const setLanguages = (values: string[]) => {
    setValue("StaffLanguage1", values[0] || "", { shouldDirty: true });
    setValue("StaffLanguage2", values[1] || "", { shouldDirty: true });
    setValue("StaffLanguage3", values[2] || "", { shouldDirty: true });
    setValue("StaffLanguage4", values[3] || "", { shouldDirty: true });
    setValue("StaffLanguage5", values[4] || "", { shouldDirty: true });
  };

  const validateWaiverField = (
    value: string | undefined,
    fieldName: string
  ): string | true => {
    const waiver = getValues("StaffWaiver");

    if (waiver !== "Y") {
      return true;
    }
    if (!value) {
      return `${fieldName} is required when waiver is Yes`;
    }
    return true;
  };

  const validateWaiverIssuedDate = (
    value: string | undefined
  ): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    const waiver = getValues("StaffWaiver");

    if (waiver !== "Y") {
      return true;
    }

    if (!value) {
      return "Waiver issued date is required when waiver is Yes";
    }

    const effectiveStartDate = getValues("EffectiveStartDate");
    if (!effectiveStartDate) {
      return true;
    }

    const issuedDate = new Date(value);
    issuedDate.setHours(0, 0, 0, 0);

    const startDate = new Date(effectiveStartDate);
    startDate.setHours(0, 0, 0, 0);

    if (isNaN(issuedDate.getTime()) || isNaN(startDate.getTime())) {
      return true;
    }

    const oneYearBefore = new Date(startDate);
    oneYearBefore.setFullYear(oneYearBefore.getFullYear() - 1);
    oneYearBefore.setHours(0, 0, 0, 0);

    if (issuedDate > startDate) {
      return "Staff Waiver Issue Date must be within a year before Effective Start Date";
    }

    if (issuedDate < oneYearBefore) {
      return "Staff Waiver Issue Date must be within a year before Effective Start Date";
    }

    return true;
  };

  const validateWaiverExpirationDate = (
    value: string | undefined
  ): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    const waiver = getValues("StaffWaiver");

    if (waiver !== "Y") {
      return true;
    }

    if (!value) {
      return "Waiver expiration date is required when waiver is Yes";
    }

    const waiverIssuedDate = getValues("StaffWaiverIssuedDate");
    if (!waiverIssuedDate) {
      return true;
    }

    const expirationDate = new Date(value);
    expirationDate.setHours(0, 0, 0, 0);

    const issuedDate = new Date(waiverIssuedDate);
    issuedDate.setHours(0, 0, 0, 0);

    if (isNaN(expirationDate.getTime()) || isNaN(issuedDate.getTime())) {
      return true;
    }

    if (expirationDate <= issuedDate) {
      return "Staff Waiver Expiration Date must be after Staff Waiver Issue Date";
    }

    return true;
  };

  const validatePermitField = (
    value: string | undefined,
    fieldName: string
  ): string | true => {
    const waiver = getValues("StaffWaiver");

    if (waiver === "Y" && value && value.trim() !== "") {
      return `${fieldName} should be empty when waiver is Yes`;
    }

    if ((waiver === "N" || !waiver) && !value) {
      return `${fieldName} is required when waiver is No or blank`;
    }

    return true;
  };

  const validatePermitIssuedDate = (
    value: string | undefined
  ): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    const permitNumber = getValues("StaffPermitOrCredentialNumber");
    const hasPermitNumber =
      permitNumber != null && String(permitNumber).trim() !== "";

    if (hasPermitNumber && !value) {
      return "Permit/Credential Issued Date is required when a Permit/Credential number is provided";
    }

    if (!value) {
      return true;
    }

    const effectiveStartDate = getValues("EffectiveStartDate");
    const dateOfBirth = getValues("StaffDateOfBirth");

    if (!effectiveStartDate) {
      return true;
    }

    const parseLocalDate = (dateStr: string): Date => {
      const parts = dateStr.split("-").map(Number);
      if (parts.length === 3) {
        return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
      }
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const issuedDate = parseLocalDate(value);
    const startDate = parseLocalDate(effectiveStartDate);

    if (isNaN(issuedDate.getTime()) || isNaN(startDate.getTime())) {
      return true;
    }

    if (issuedDate > startDate) {
      return "Staff Permit Issued Date must be on or before Effective Start Date and after staff turned 18";
    }

    if (dateOfBirth) {
      const birthDate = parseLocalDate(dateOfBirth);

      if (!isNaN(birthDate.getTime())) {
        const age18Date = new Date(birthDate);
        age18Date.setFullYear(age18Date.getFullYear() + 18);

        if (issuedDate < age18Date) {
          return "Staff Permit Issued Date must be on or before Effective Start Date and after staff turned 18";
        }
      }
    }

    return true;
  };

  const validatePermitExpirationDate = (
    value: string | undefined
  ): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    const waiver = getValues("StaffWaiver");
    const permitNumber = getValues("StaffPermitOrCredentialNumber");
    const hasPermitNumber =
      permitNumber != null && String(permitNumber).trim() !== "";

    if (hasPermitNumber && !value) {
      return "Permit/Credential Expiration Date is required when a Permit/Credential number is provided";
    }

    if (waiver === "Y" && value && value.trim() !== "") {
      return "Permit expiration date should be empty when waiver is Yes";
    }

    if (!value || waiver === "Y") {
      return true;
    }

    const permitIssuedDate = getValues("StaffPermitIssuedDate");
    if (!permitIssuedDate) {
      return true;
    }

    const expirationDate = new Date(value);
    expirationDate.setHours(0, 0, 0, 0);

    const issuedDate = new Date(permitIssuedDate);
    issuedDate.setHours(0, 0, 0, 0);

    if (isNaN(expirationDate.getTime()) || isNaN(issuedDate.getTime())) {
      return true;
    }

    if (expirationDate <= issuedDate) {
      return "Staff Permit Expiration Date must be after Staff Permit Issued Date";
    }

    return true;
  };

  const validatePermitNumber = (value: string | undefined): string | true => {
    const waiver = getValues("StaffWaiver");

    if (waiver === "Y" && value && value.trim() !== "") {
      return "Permit/Credential number should be empty when waiver is Yes";
    }

    return true;
  };

  const validatePermitType = (value: string | undefined): string | true => {
    const permitNumber = getValues("StaffPermitOrCredentialNumber");
    const hasPermitNumber =
      permitNumber != null && String(permitNumber).trim() !== "";

    if (hasPermitNumber && (!value || value.trim() === "")) {
      return "Permit/Credential type is required when a Permit/Credential number is provided";
    }

    return true;
  };

  const validateCurrentEmploymentStartDate = (
    value: string | undefined
  ): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    if (!value || value.trim() === "") {
      return "Current Employment Start Date is required";
    }

    const effectiveStartDate = getValues("EffectiveStartDate");
    if (!effectiveStartDate) return true;

    const currentEmpDate = new Date(value);
    currentEmpDate.setHours(0, 0, 0, 0);

    const effectiveDate = new Date(effectiveStartDate);
    effectiveDate.setHours(0, 0, 0, 0);

    if (isNaN(currentEmpDate.getTime()) || isNaN(effectiveDate.getTime())) {
      return true;
    }

    if (currentEmpDate > effectiveDate) {
      return "Staff Current Employment Start Date must be before Effective Start Date";
    }

    return true;
  };

  const validateOverallEmploymentStartDate = (
    value: string | undefined
  ): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    if (!value || value.trim() === "") {
      return "Overall Employment Start Date is required";
    }

    const currentEmploymentStartDate = getValues(
      "StaffCurrentEmploymentStartDate"
    );
    if (!currentEmploymentStartDate) return true;

    const overallDate = new Date(value);
    overallDate.setHours(0, 0, 0, 0);

    const currentDate = new Date(currentEmploymentStartDate);
    currentDate.setHours(0, 0, 0, 0);

    if (isNaN(overallDate.getTime()) || isNaN(currentDate.getTime())) {
      return true;
    }

    if (overallDate > currentDate) {
      return "Staff Overall Employment Start Date must be before Staff Current Employment Start Date";
    }

    return true;
  };

  const validateEffectiveStartDate = (
    value: string | undefined
  ): string | true => {
    const minCheck = validateMinDate(value);
    if (minCheck !== true) return minCheck;

    if (!value || value.trim() === "") {
      return "Effective Start Date is required";
    }

    const programYear = getValues("ProgramYear");
    if (!programYear) return true;

    const programYearMatch = programYear.match(/^(\d{4})-(\d{4})$/);
    if (!programYearMatch) return true;

    const startYear = parseInt(programYearMatch[1], 10);
    const endYear = parseInt(programYearMatch[2], 10);

    const programYearStart = new Date(startYear, 6, 1, 0, 0, 0, 0);
    const programYearEnd = new Date(endYear, 5, 30, 23, 59, 59, 999);

    let effectiveDate: Date;

    if (value.includes("/")) {
      const [month, day, year] = value.split("/").map(Number);
      effectiveDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    } else if (value.includes("-")) {
      const [year, month, day] = value.split("-").map(Number);
      effectiveDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    } else {
      effectiveDate = new Date(value);
      effectiveDate.setHours(0, 0, 0, 0);
    }

    if (isNaN(effectiveDate.getTime())) return true;

    if (effectiveDate < programYearStart || effectiveDate > programYearEnd) {
      return `Effective Start Date must be between 07/01/${startYear} and 06/30/${endYear} for program year ${programYear}`;
    }

    return true;
  };

  const triggerWaiverValidation = useCallback(async (): Promise<boolean> => {
    const waiver = getValues("StaffWaiver");
    if (waiver === "Y") {
      return await trigger([
        "StaffWaiverIssuedDate",
        "StaffWaiverExpirationDate",
        "StaffWaiverType",
      ]);
    }
    return true;
  }, [getValues, trigger]);

  return {
    getRaces,
    setRaces,
    getLanguages,
    setLanguages,
    validateWaiverField,
    validateWaiverIssuedDate,
    validateWaiverExpirationDate,
    validatePermitField,
    validatePermitIssuedDate,
    validatePermitExpirationDate,
    validatePermitNumber,
    validatePermitType,
    validateCurrentEmploymentStartDate,
    validateOverallEmploymentStartDate,
    validateEffectiveStartDate,
    trigger,
    triggerWaiverValidation,
    formatDateOnly,
  };
}
